// Disposition math, client twin of utils/dispatch/calc.js (conflict preview while dragging,
// week grid, travel legs; the server stays authoritative). Keep everything below the header
// identical to the server file: test/dispatch/calc.test.js compares both.

const TZ = "Europe/Berlin";
const DAY_MS = 86400000;
const MINUTE_MS = 60000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const CLOCK_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
// "warning" needs the planner's acknowledgement before saving, "info" is only shown
const SEVERITIES = ["warning", "info"];
const SOON_DAYS = 30;
const MAX_DAYS = 31;
const DEFAULT_SETTINGS = {
  // ISO weekdays (1 = Monday) and hours of the company's normal working day; employees planned
  // in shifts are checked against their shifts instead
  workdays: [1, 2, 3, 4, 5],
  dayStart: "07:00",
  dayEnd: "16:00",
  // Postcode of the Betriebshof, where the first trip of the day starts
  homeZip: "",
  // Straight line × factor = road distance; average speed incl. town traffic
  travelFactor: 1.3,
  speedKmh: 50,
  // Nominal distances where postcode centroids cannot tell: same postcode, same Leitregion
  localKm: 5,
  regionKm: 15
};
// Fahrerlaubnisklassen a licence class includes (FeV § 6 Abs. 3, simplified to what a Handwerk
// fleet needs: Pkw, Anhänger, Transporter bis 7,5 t, Lkw)
const LICENSE_INCLUDES = {
  B: ["B"],
  B96: ["B", "B96"],
  BE: ["B", "B96", "BE"],
  C1: ["C1"],
  C1E: ["C1", "C1E", "BE"],
  C: ["C", "C1"],
  CE: ["C", "C1", "CE", "C1E", "BE", "T"],
  T: ["T", "L"],
  L: ["L"]
};
const LICENSE_CLASSES = Object.keys(LICENSE_INCLUDES);

const idOf = value => (value == null ? "" : String(value._id || value));

const timeOf = value => (value instanceof Date ? value.getTime() : new Date(value).getTime());

/** A real calendar day "YYYY-MM-DD" (2026-02-30 is not). */
const isDay = value => {
  if (typeof value !== "string" || !DAY_RE.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};

const addDays = (day, days) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

const daysBetween = (a, b) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);

/** ISO weekday of a day: 1 = Monday … 7 = Sunday. */
const weekdayOf = day => {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return weekday === 0 ? 7 : weekday;
};

/** Monday of the week containing `day`. */
const mondayOf = day => addDays(day, 1 - weekdayOf(day));

const clockToMinutes = value => {
  const match = CLOCK_RE.exec(String(value || ""));
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const minutesToClock = minutes => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

const PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23"
});

/** { day: "YYYY-MM-DD", minutes } of an instant on the Berlin wall clock. */
const berlinParts = value => {
  const parts = {};
  PARTS.formatToParts(new Date(timeOf(value))).forEach(p => (parts[p.type] = p.value));
  return {
    day: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute)
  };
};

const offsetAt = time => {
  const { day, minutes } = berlinParts(time);
  const wall = Date.parse(`${day}T00:00:00Z`) + minutes * MINUTE_MS;
  return wall - Math.floor(time / MINUTE_MS) * MINUTE_MS;
};

/** The instant of a Berlin wall-clock time (minutes after midnight of `day`). */
const berlinInstant = (day, minutes) => {
  const naive = Date.parse(`${day}T00:00:00Z`) + minutes * MINUTE_MS;
  let guess = naive - offsetAt(naive);
  guess = naive - offsetAt(guess);
  return new Date(guess);
};

const dayOf = value => berlinParts(value).day;

/** Berlin days a range [start, end) touches; ending at midnight does not touch the next day. */
const daysOfRange = range => {
  const first = dayOf(range.start);
  const last = dayOf(Math.max(timeOf(range.start), timeOf(range.end) - 1));
  const out = [];
  for (let day = first; day <= last && out.length <= MAX_DAYS; day = addDays(day, 1)) {
    out.push(day);
  }
  return out;
};

/** Half-open ranges overlap; touching (one ends when the other starts) is fine. */
const overlaps = (a, b) => timeOf(a.start) < timeOf(b.end) && timeOf(b.start) < timeOf(a.end);

/** Spans sorted by start, touching or overlapping ones merged. */
const mergeSpans = spans => {
  const out = [];
  [...(spans || [])]
    .map(s => ({ start: timeOf(s.start), end: timeOf(s.end) }))
    .filter(s => s.end > s.start)
    .sort((a, b) => a.start - b.start)
    .forEach(s => {
      const last = out[out.length - 1];
      if (last && s.start <= last.end) last.end = Math.max(last.end, s.end);
      else out.push({ ...s });
    });
  return out;
};

const isCovered = (range, spans) =>
  mergeSpans(spans).some(s => s.start <= timeOf(range.start) && s.end >= timeOf(range.end));

const toNumber = (value, fallback, min, max) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/** The company's dispatch settings with defaults filled in and nonsense dropped. */
const normalizeSettings = raw => {
  const source = raw || {};
  const workdays = Array.isArray(source.workdays)
    ? [...new Set(source.workdays.map(Number).filter(d => Number.isInteger(d) && d >= 1 && d <= 7))]
    : DEFAULT_SETTINGS.workdays;
  let dayStart =
    clockToMinutes(source.dayStart) == null ? DEFAULT_SETTINGS.dayStart : source.dayStart;
  let dayEnd = clockToMinutes(source.dayEnd) == null ? DEFAULT_SETTINGS.dayEnd : source.dayEnd;
  if (clockToMinutes(dayEnd) <= clockToMinutes(dayStart)) {
    dayStart = DEFAULT_SETTINGS.dayStart;
    dayEnd = DEFAULT_SETTINGS.dayEnd;
  }
  const zip = String(source.homeZip || "").trim();
  return {
    workdays: workdays.sort((a, b) => a - b),
    dayStart,
    dayEnd,
    homeZip: /^\d{5}$/.test(zip) ? zip : "",
    travelFactor: toNumber(source.travelFactor, DEFAULT_SETTINGS.travelFactor, 1, 3),
    speedKmh: toNumber(source.speedKmh, DEFAULT_SETTINGS.speedKmh, 10, 130),
    localKm: toNumber(source.localKm, DEFAULT_SETTINGS.localKm, 0, 50),
    regionKm: toNumber(source.regionKm, DEFAULT_SETTINGS.regionKm, 0, 100)
  };
};

/**
 * The normal working hours from `from` to `to` (days, inclusive) as instants: one span per
 * working day. `workdays` are ISO weekdays (an employee's own working days, else the company's).
 */
const workSpans = ({ from, to, workdays, dayStart, dayEnd }) => {
  const startMinutes = clockToMinutes(dayStart);
  const endMinutes = clockToMinutes(dayEnd);
  const out = [];
  if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) return out;
  for (let day = from; day <= to && out.length <= MAX_DAYS; day = addDays(day, 1)) {
    if (!(workdays || []).includes(weekdayOf(day))) continue;
    out.push({ start: berlinInstant(day, startMinutes), end: berlinInstant(day, endMinutes) });
  }
  return out;
};

/** "B, BE c1" → ["B", "BE", "C1"] (known classes only). */
const parseLicenseClasses = text => [
  ...new Set(
    String(text || "")
      .toUpperCase()
      .split(/[^A-Z0-9]+/)
      .filter(c => LICENSE_INCLUDES[c])
  )
];

const licenseCovers = (classes, required) =>
  !required || classes.some(c => (LICENSE_INCLUDES[c] || [c]).includes(required));

/** The record of `type` that is valid longest (no end date = unlimited). */
const bestRecord = (records, type) =>
  (records || [])
    .filter(r => r.type === type)
    .sort((a, b) => {
      if (!a.validUntil) return -1;
      if (!b.validUntil) return 1;
      return b.validUntil.localeCompare(a.validUntil);
    })[0] || null;

const issue = (type, severity, fields) => ({ type, severity, ...fields });

/**
 * Everything that speaks against planning `candidate`:
 *   candidate { id, start, end, employees, vehicle, tools, driver, requiredSkills,
 *               requiredQualifications }
 *   ctx {
 *     bookings: other planned items [{ id, title, start, end, employees, vehicle, tools }],
 *     absences: [{ employee, from, to, kind: "leave"|"sick"|"school", label }],
 *     holidays: { "YYYY-MM-DD": name },
 *     workSpans: { [employee]: [{ start, end }] }  (an employee missing here is not checked),
 *     people: { [employee]: { name, skills } },
 *     qualifications: { [employee]: [{ type, title, validUntil, classes }] },
 *     vehicles: { [id]: { name, plate, seats, licenseClass, nextInspection } },
 *     tools: { [id]: { title } }
 *   }
 * Returns [{ type, severity, employee?, vehicle?, tool?, ref?, day, params }], warnings first.
 */
const findConflicts = (candidate, ctx = {}) => {
  const out = [];
  if (!candidate || !candidate.start || !candidate.end) return out;
  const range = { start: candidate.start, end: candidate.end };
  if (timeOf(range.end) <= timeOf(range.start)) return out;
  const days = daysOfRange(range);
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  const employees = [...new Set((candidate.employees || []).map(idOf).filter(Boolean))];
  const vehicleId = idOf(candidate.vehicle);
  const toolIds = [...new Set((candidate.tools || []).map(idOf).filter(Boolean))];
  const people = ctx.people || {};
  const nameOf = id => (people[id] && people[id].name) || id;
  const self = idOf(candidate.id);

  // Double bookings
  for (const other of ctx.bookings || []) {
    if (self && idOf(other.id) === self) continue;
    if (!other.start || !other.end || !overlaps(range, other)) continue;
    const ref = {
      id: idOf(other.id),
      title: other.title || "",
      start: new Date(timeOf(other.start)).toISOString(),
      end: new Date(timeOf(other.end)).toISOString()
    };
    const busy = (other.employees || []).map(idOf);
    employees
      .filter(id => busy.includes(id))
      .forEach(id =>
        out.push(
          issue("person_double", "warning", {
            employee: id,
            day: dayOf(Math.max(timeOf(range.start), timeOf(other.start))),
            ref,
            params: { name: nameOf(id), title: ref.title }
          })
        )
      );
    if (vehicleId && idOf(other.vehicle) === vehicleId) {
      const vehicle = (ctx.vehicles || {})[vehicleId] || {};
      out.push(
        issue("vehicle_double", "warning", {
          vehicle: vehicleId,
          day: dayOf(Math.max(timeOf(range.start), timeOf(other.start))),
          ref,
          params: { vehicle: vehicle.name || vehicle.plate || vehicleId, title: ref.title }
        })
      );
    }
    const otherTools = (other.tools || []).map(idOf);
    toolIds
      .filter(id => otherTools.includes(id))
      .forEach(id =>
        out.push(
          issue("tool_double", "warning", {
            tool: id,
            day: dayOf(Math.max(timeOf(range.start), timeOf(other.start))),
            ref,
            params: { tool: ((ctx.tools || {})[id] || {}).title || id, title: ref.title }
          })
        )
      );
  }

  // Absences: leave, sick leave, Berufsschule
  for (const absence of ctx.absences || []) {
    const id = idOf(absence.employee);
    if (!employees.includes(id)) continue;
    if (absence.to < firstDay || absence.from > lastDay) continue;
    const kind = ["leave", "sick", "school"].includes(absence.kind) ? absence.kind : "leave";
    out.push(
      issue(kind, "warning", {
        employee: id,
        day: absence.from > firstDay ? absence.from : firstDay,
        params: { name: nameOf(id), label: absence.label || "" }
      })
    );
  }
  days
    .filter(day => (ctx.holidays || {})[day])
    .forEach(day =>
      out.push(issue("holiday", "info", { day, params: { label: ctx.holidays[day] } }))
    );

  // Working hours (shifts or the normal working day)
  const spans = ctx.workSpans || {};
  employees
    .filter(id => spans[id] && !isCovered(range, spans[id]))
    .forEach(id =>
      out.push(
        issue("outside_hours", "warning", {
          employee: id,
          day: firstDay,
          params: { name: nameOf(id) }
        })
      )
    );

  // Skills (free tags of the dispatch profile)
  const required = (candidate.requiredSkills || [])
    .map(s => String(s || "").trim())
    .filter(Boolean);
  if (required.length) {
    employees.forEach(id => {
      const own = ((people[id] && people[id].skills) || []).map(s => String(s).toLowerCase());
      const missing = required.filter(s => !own.includes(s.toLowerCase()));
      if (missing.length) {
        out.push(
          issue("skill_missing", "warning", {
            employee: id,
            day: firstDay,
            params: { name: nameOf(id), skills: missing.join(", ") }
          })
        );
      }
    });
  }

  // Qualifications and checks (Arbeitsschutz records)
  const qualifications = ctx.qualifications || {};
  const soonDays = Number.isFinite(ctx.soonDays) ? ctx.soonDays : SOON_DAYS;
  const qualificationIssue = (id, record, type, label) => {
    if (!record)
      return issue(`${label}_missing`, "warning", {
        employee: id,
        day: firstDay,
        params: { name: nameOf(id), qualification: type }
      });
    if (!record.validUntil) return null;
    if (record.validUntil < lastDay) {
      return issue(`${label}_expired`, "warning", {
        employee: id,
        day: firstDay,
        params: { name: nameOf(id), qualification: type, date: record.validUntil }
      });
    }
    if (daysBetween(lastDay, record.validUntil) <= soonDays) {
      return issue(`${label}_expiring`, "info", {
        employee: id,
        day: firstDay,
        params: { name: nameOf(id), qualification: type, date: record.validUntil }
      });
    }
    return null;
  };
  [...new Set(candidate.requiredQualifications || [])].forEach(type =>
    employees.forEach(id => {
      const found = qualificationIssue(
        id,
        bestRecord(qualifications[id], type),
        type,
        "qualification"
      );
      if (found) out.push(found);
    })
  );

  // Vehicle: inspection date, seats, the driver's licence check and class
  if (vehicleId) {
    const vehicle = (ctx.vehicles || {})[vehicleId] || {};
    const vehicleName = vehicle.name || vehicle.plate || vehicleId;
    if (vehicle.nextInspection && isDay(vehicle.nextInspection)) {
      if (vehicle.nextInspection < lastDay) {
        out.push(
          issue("vehicle_inspection", "warning", {
            vehicle: vehicleId,
            day: firstDay,
            params: { vehicle: vehicleName, date: vehicle.nextInspection }
          })
        );
      } else if (daysBetween(lastDay, vehicle.nextInspection) <= soonDays) {
        out.push(
          issue("vehicle_inspection_soon", "info", {
            vehicle: vehicleId,
            day: firstDay,
            params: { vehicle: vehicleName, date: vehicle.nextInspection }
          })
        );
      }
    }
    if (Number(vehicle.seats) > 0 && employees.length > Number(vehicle.seats)) {
      out.push(
        issue("vehicle_seats", "info", {
          vehicle: vehicleId,
          day: firstDay,
          params: { vehicle: vehicleName, seats: Number(vehicle.seats), people: employees.length }
        })
      );
    }
    const driver = employees.includes(idOf(candidate.driver))
      ? idOf(candidate.driver)
      : employees[0];
    if (driver) {
      const record = bestRecord(qualifications[driver], "FUEHRERSCHEIN");
      const found = qualificationIssue(driver, record, "FUEHRERSCHEIN", "license");
      if (found) out.push({ ...found, vehicle: vehicleId });
      if (record && vehicle.licenseClass) {
        const classes = parseLicenseClasses(record.classes);
        if (!classes.length) {
          out.push(
            issue("license_class_unknown", "info", {
              employee: driver,
              vehicle: vehicleId,
              day: firstDay,
              params: { name: nameOf(driver), licenseClass: vehicle.licenseClass }
            })
          );
        } else if (!licenseCovers(classes, vehicle.licenseClass)) {
          out.push(
            issue("license_class", "warning", {
              employee: driver,
              vehicle: vehicleId,
              day: firstDay,
              params: {
                name: nameOf(driver),
                licenseClass: vehicle.licenseClass,
                classes: classes.join(", ")
              }
            })
          );
        }
      }
    }
  }

  return out.sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity));
};

const needsAcknowledgement = conflicts => (conflicts || []).some(c => c.severity === "warning");

/** Great-circle distance in km. */
const haversineKm = (a, b) => {
  const rad = d => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

/**
 * Road distance and driving time between two places known by postcode ({ zip, lat, lng }; the
 * coordinates are the Leitregion centre, ±30 km). Same postcode or same Leitregion get a
 * nominal distance, since the centroids cannot tell. null when a place is unknown.
 */
const estimateTravel = (from, to, rawSettings) => {
  const settings = normalizeSettings(rawSettings);
  if (!from || !to || !from.zip || !to.zip) return null;
  let km;
  let accuracy;
  if (from.zip === to.zip) {
    km = settings.localKm;
    accuracy = "local";
  } else {
    if (![from.lat, from.lng, to.lat, to.lng].every(Number.isFinite)) return null;
    const road = haversineKm(from, to) * settings.travelFactor;
    if (from.zip.slice(0, 2) === to.zip.slice(0, 2)) {
      km = Math.max(settings.regionKm, road);
      accuracy = "region";
    } else {
      km = road;
      accuracy = "estimate";
    }
  }
  km = Math.round(km);
  const minutes = km === 0 ? 0 : Math.ceil(((km / settings.speedKmh) * 60) / 5) * 5;
  return { km, minutes, accuracy };
};

/**
 * Trips of one person on one day: from the Betriebshof (`home`, may be null) to the first job
 * and between consecutive jobs. Each leg { from, to, km, minutes, accuracy, gapMinutes, tight }
 * where tight = the time between the jobs is shorter than the drive.
 */
const travelLegs = (items, home, settings) => {
  const sorted = [...(items || [])]
    .filter(i => i.start && i.end)
    .sort((a, b) => timeOf(a.start) - timeOf(b.start));
  const legs = [];
  let previous = home ? { id: "home", place: home, end: null } : null;
  for (const item of sorted) {
    if (previous && item.place) {
      const estimate = estimateTravel(previous.place, item.place, settings);
      if (estimate) {
        const gapMinutes =
          previous.end == null
            ? null
            : Math.round((timeOf(item.start) - timeOf(previous.end)) / MINUTE_MS);
        legs.push({
          from: previous.id,
          to: idOf(item.id),
          ...estimate,
          gapMinutes,
          tight: gapMinutes != null && gapMinutes < estimate.minutes
        });
      }
    }
    if (item.place) previous = { id: idOf(item.id), place: item.place, end: item.end };
  }
  return legs;
};

/**
 * The board of `days` (consecutive days from `from`): one row per employee with, per day, the
 * ids of their items (sorted by start) and their absences; items without anyone are listed per
 * day under `unassigned`. Holidays mark the day.
 */
const buildGrid = ({ from, days, employees, items, absences, holidays }) => {
  const count = Math.min(Math.max(Number(days) || 7, 1), MAX_DAYS);
  const columns = [];
  for (let i = 0; i < count; i += 1) {
    const day = addDays(from, i);
    columns.push({ day, weekday: weekdayOf(day), holiday: (holidays || {})[day] || "" });
  }
  const inGrid = new Set(columns.map(c => c.day));
  const emptyDays = () =>
    Object.fromEntries(columns.map(c => [c.day, { items: [], absences: [] }]));
  const rows = (employees || []).map(e => ({
    employee: idOf(e.id || e._id || e),
    days: emptyDays()
  }));
  const rowOf = new Map(rows.map(r => [r.employee, r]));
  const unassigned = Object.fromEntries(columns.map(c => [c.day, []]));
  const sorted = [...(items || [])]
    .filter(i => i.start && i.end)
    .sort(
      (a, b) =>
        timeOf(a.start) - timeOf(b.start) ||
        String(a.title || "").localeCompare(String(b.title || ""))
    );
  for (const item of sorted) {
    const people = (item.employees || []).map(idOf);
    for (const day of daysOfRange(item).filter(d => inGrid.has(d))) {
      if (!people.length) unassigned[day].push(idOf(item.id));
      people.forEach(id => {
        const row = rowOf.get(id);
        if (row) row.days[day].items.push(idOf(item.id));
      });
    }
  }
  for (const absence of absences || []) {
    const row = rowOf.get(idOf(absence.employee));
    if (!row) continue;
    columns
      .filter(c => c.day >= absence.from && c.day <= absence.to)
      .forEach(c =>
        row.days[c.day].absences.push({ kind: absence.kind, label: absence.label || "" })
      );
  }
  return { days: columns, rows, unassigned };
};

/**
 * Moves a range to another day and/or start time keeping its duration: `day` the new Berlin
 * day, `startMinutes` the new start (default: the old wall-clock start).
 */
const shiftRange = (range, { day, startMinutes } = {}) => {
  const parts = berlinParts(range.start);
  const duration = timeOf(range.end) - timeOf(range.start);
  const start = berlinInstant(
    day || parts.day,
    startMinutes == null ? parts.minutes : startMinutes
  );
  return { start, end: new Date(start.getTime() + duration) };
};

export {
  TZ,
  SEVERITIES,
  SOON_DAYS,
  MAX_DAYS,
  DEFAULT_SETTINGS,
  LICENSE_CLASSES,
  isDay,
  addDays,
  daysBetween,
  weekdayOf,
  mondayOf,
  clockToMinutes,
  minutesToClock,
  berlinParts,
  berlinInstant,
  dayOf,
  daysOfRange,
  overlaps,
  mergeSpans,
  isCovered,
  normalizeSettings,
  workSpans,
  parseLicenseClasses,
  licenseCovers,
  findConflicts,
  needsAcknowledgement,
  haversineKm,
  estimateTravel,
  travelLegs,
  buildGrid,
  shiftRange
};
